import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CBR_QUESTIONS from '../data/cbrQuestions';
import { Button } from './ui/button';
import { CheckCircle, XCircle, Car } from '@phosphor-icons/react';

function shuffleArray(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

// Weights per category — Banden en Motorkap komen relatief vaker aan bod
// omdat dat de meest gestelde examen-controlevragen zijn.
const CATEGORY_WEIGHTS = {
    'Banden': 2.0,
    'Motorkap & Vloeistoffen': 2.0,
    'Dashboard': 1.0,
    'Lichten': 1.0,
};

function QuestionImage({ src, highlight }) {
    return (
        <div className="relative w-full rounded-lg overflow-hidden border border-[#4A5568] mb-4" data-testid="question-image">
            <img src={src} alt="Voertuigonderdeel" className="w-full h-auto block" />
            {highlight && (
                <span
                    className="absolute rounded-full border-[3px] border-[#D4A537] shadow-[0_0_0_3px_rgba(0,0,0,0.55),0_0_18px_rgba(212,165,55,0.9)] animate-pulse pointer-events-none"
                    style={{
                        left: `${highlight.x}%`, top: `${highlight.y}%`,
                        width: `max(${highlight.r * 2}%, 26px)`, aspectRatio: '1 / 1',
                        transform: 'translate(-50%, -50%)',
                    }}
                    data-testid="question-image-highlight"
                />
            )}
        </div>
    );
}

const SEEN_STORAGE_KEY = 'cbr_seen_question_ids';

function loadSeenIds() {
    try {
        const raw = localStorage.getItem(SEEN_STORAGE_KEY);
        return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
}

function saveSeenIds(set) {
    try {
        localStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify([...set]));
    } catch { /* quota exceeded — ignore */ }
}

function pickWeightedRandom(candidates) {
    // Weighted random selection using CATEGORY_WEIGHTS
    const total = candidates.reduce((sum, q) => sum + (CATEGORY_WEIGHTS[q.category] || 1), 0);
    let r = Math.random() * total;
    for (const q of candidates) {
        r -= (CATEGORY_WEIGHTS[q.category] || 1);
        if (r <= 0) return q;
    }
    return candidates[candidates.length - 1];
}

function getRandomQuestion() {
    const seen = loadSeenIds();
    let candidates = CBR_QUESTIONS.filter(q => !seen.has(q.id));
    // If the student has seen all questions once → reset and start over
    if (candidates.length === 0) {
        seen.clear();
        saveSeenIds(seen);
        candidates = CBR_QUESTIONS;
    }
    const chosen = pickWeightedRandom(candidates);
    seen.add(chosen.id);
    saveSeenIds(seen);
    return chosen;
}

export default function DailyQuestion({ onComplete }) {
    const [question] = useState(() => getRandomQuestion());
    const [shuffledAnswers] = useState(() => shuffleArray(question.answers));
    const [selected, setSelected] = useState(null);
    const [answered, setAnswered] = useState(false);

    const handleSelect = (index) => {
        if (answered) return;
        setSelected(index);
        setAnswered(true);
    };

    const isCorrect = selected !== null && shuffledAnswers[selected]?.correct;

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        >
            <motion.div
                initial={{ y: 20 }}
                animate={{ y: 0 }}
                className="bg-[#2D3748] border border-[#4A5568] rounded-xl max-w-lg w-full shadow-2xl overflow-y-auto max-h-[92vh]"
                data-testid="daily-question-modal"
            >
                {/* Header */}
                <div className="bg-[#D4A537] px-5 py-3 flex items-center gap-3">
                    <Car size={24} weight="fill" className="text-[#1A202C]" />
                    <div>
                        <h2 className="text-[#1A202C] font-bold text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Dagelijkse CBR Vraag
                        </h2>
                        <p className="text-[#1A202C]/70 text-xs" data-testid="daily-question-category">{question.category}</p>
                    </div>
                </div>

                {/* Question */}
                <div className="p-5">
                    {question.image && <QuestionImage src={question.image} highlight={question.highlight} />}
                    <p className="text-white text-lg font-medium mb-5 leading-relaxed" data-testid="daily-question-text">
                        {question.question}
                    </p>

                    {/* Answers */}
                    <div className="space-y-3">
                        {shuffledAnswers.map((answer, i) => {
                            let borderColor = '#4A5568';
                            let bgColor = '#1A202C';
                            let textColor = 'text-gray-200';

                            if (answered) {
                                if (answer.correct) {
                                    borderColor = '#10B981';
                                    bgColor = '#10B981/15';
                                    textColor = 'text-emerald-300';
                                } else if (i === selected && !answer.correct) {
                                    borderColor = '#EF4444';
                                    bgColor = '#EF4444/10';
                                    textColor = 'text-red-300';
                                }
                            }

                            return (
                                <motion.button
                                    key={i}
                                    whileTap={!answered ? { scale: 0.98 } : {}}
                                    onClick={() => handleSelect(i)}
                                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${textColor} ${
                                        !answered ? 'hover:border-[#D4A537] hover:bg-[#D4A537]/5 cursor-pointer' : 'cursor-default'
                                    } ${i === selected && !answered ? 'border-[#D4A537] bg-[#D4A537]/10' : ''}`}
                                    style={{ borderColor, backgroundColor: answered ? (answer.correct ? 'rgba(16,185,129,0.1)' : i === selected ? 'rgba(239,68,68,0.1)' : '#1A202C') : '#1A202C' }}
                                    data-testid={`answer-${i}`}
                                >
                                    <div className="flex items-start gap-3">
                                        <span className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5 ${
                                            answered && answer.correct ? 'border-emerald-500 bg-emerald-500 text-white' :
                                            answered && i === selected && !answer.correct ? 'border-red-500 bg-red-500 text-white' :
                                            'border-[#4A5568] text-gray-500'
                                        }`}>
                                            {answered && answer.correct ? <CheckCircle size={16} weight="fill" /> :
                                             answered && i === selected && !answer.correct ? <XCircle size={16} weight="fill" /> :
                                             String.fromCharCode(65 + i)}
                                        </span>
                                        <span className="text-sm leading-relaxed">{answer.text}</span>
                                    </div>
                                </motion.button>
                            );
                        })}
                    </div>

                    {/* Result + continue */}
                    <AnimatePresence>
                        {answered && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="mt-5"
                            >
                                <div className={`p-3 rounded-lg text-sm mb-4 ${
                                    isCorrect ? 'bg-emerald-900/30 border border-emerald-700 text-emerald-300' : 'bg-red-900/30 border border-red-700 text-red-300'
                                }`}>
                                    <div className="font-semibold mb-1">
                                        {isCorrect ? '✅ Goed zo!' : '❌ Helaas, dat was niet juist.'}
                                    </div>
                                    {question.explanation && (
                                        <div className="text-gray-300 text-xs mt-1 leading-relaxed">
                                            💡 {question.explanation}
                                        </div>
                                    )}
                                </div>
                                <Button
                                    onClick={onComplete}
                                    className="w-full bg-[#D4A537] text-[#1A202C] hover:bg-[#E5B84A] font-semibold py-3"
                                    data-testid="continue-to-dashboard"
                                >
                                    Doorgaan naar mijn leskaart
                                </Button>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>
        </motion.div>
    );
}
